import axios, { AxiosError } from "axios";
import { deleteToken } from "firebase/messaging";
import { makeAutoObservable } from "mobx";
import { persist } from "mobx-persist";
import { unregisterServiceWorker } from "serviceWorker";

import api from "../axios.config";
import { messaging } from "../firebase";
import { queryClient } from "../query-client";
import { RootStore } from "./RootStore";

interface UserDetails {
  customerType: CustomerType;
  customerTypeLogo: string;
  firstName: string;
  lastName: string;
  activated: boolean;
  unitCode: string;
  tradeFooter: string;
  tradeHeader: string;
  latitude: number;
  longitude: number;
  subscription: {
    type: string;
    validUntil: string;
  };
}

export interface CurrentUser {
  expiresAt: number;
  id: string;
  refreshToken: string;
  token: string;
  tokenType: string;
}
export interface CurrentUserDetails {
  aadhaarCardNo: string;
  addressData: {
    landmark: string;
    addressLine1: string;
    cityOrTown: string;
    district: string;
    latitude: number;
    location: string;
    longitude: number;
    pincode: number;
    placeId: string;
    state: string;
  };
  metaData: {
    activated: boolean;
    pageType: string;
    userRole: string;
  };
  customerType: CustomerType;
  customerTypeLogo: string;
  drugLicenseNo1: string;
  drugLicenseNo2: string;
  drugLicenseUrl: string;
  email: string;
  firstName: string;
  gstin: string;
  isDocumentUploaded: boolean;
  lastName: string;
  medicoStoresLogo: string;
  message: string;
  mimeType: MimeType;
  mobileNumber: string;
  panNumber: string;
  tradeFooter: string;
  tradeHeader: string;
  tradeName: string;
  unitCode: string;
  subscription: {
    type: "TRIAL" | "ULTIMATE";
    validUntil: string;
    validUntilMs: number;
  };
}

type MimeType = "image/png" | "image/jpg" | "image/jpeg" | "application/pdf";

export interface CurrentUserDetailsResponse {
  body: CurrentUserDetails;
  type: ResponseStatus;
}
export interface LoginResponse {
  body: {
    username: string;
    mobileNumber: number;
    uid: string;
  };
  error: {
    body: string;
    title: string;
  };
  type: ResponseStatus;
}

export interface RefreshTokenResponse {
  body: {
    token: string;
    tokenType: string;
    id: string;
    refreshToken: string;
    expiresAt: number;
  };
  type: ResponseStatus;
}

class UserStore {
  rootStore: RootStore;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this);
  }

  @persist("object") currentUser: CurrentUser | null = null;

  currentUserDetails: CurrentUserDetails | null = null;

  @persist("object") userDetails: UserDetails | null = null;

  tokenType = "Bearer";

  @persist currentFcmToken = "";

  @persist currentOsType = "";

  @persist currentDeviceId = "";

  @persist currentDeviceType = "";

  @persist backendFcmToken = "";

  fcmTokenLoading = false;

  isRefreshTokenLoading = false;

  isDetailsLoading = false;

  setIsDetailsLoading = (value: boolean): void => {
    this.isDetailsLoading = value;
  };

  setIsRefreshTokenLoading = (value: boolean): void => {
    this.isRefreshTokenLoading = value;
  };

  setFcmTokenLoading = (value: boolean): void => {
    this.fcmTokenLoading = value;
  };

  setBackendFcmToken = (fcmToken: string): void => {
    this.backendFcmToken = fcmToken;
  };

  setCurrentOsType = (osType: string): void => {
    this.currentOsType = osType;
  };

  setCurrentDeviceType = (devicetype: string): void => {
    this.currentDeviceType = devicetype;
  };

  setCurrentDeviceID = async (deviceId: string): Promise<void> => {
    const thisDeviceID = deviceId;
    this.currentDeviceId = thisDeviceID;
  };

  // setCurrentDeviceID = async (deviceId: string): Promise<void> => {
  //   this.currentDeviceId = deviceId;
  // };

  setCurrentFmcToken = (fcmToken: string): void => {
    this.currentFcmToken = fcmToken;
  };

  setUserDetails = (details: UserDetails | null): void => {
    this.userDetails = details;
  };

  setTokenType = (tokenType: string): void => {
    this.tokenType = tokenType;
  };

  setCurrentUser = (user: CurrentUser | null): void => {
    this.currentUser = user;
  };

  setCurrentUserDetails = (user: CurrentUserDetails | null): void => {
    this.currentUserDetails = user;

    if (user) {
      const {
        customerType,
        customerTypeLogo,
        firstName,
        lastName,
        metaData,
        unitCode,
        tradeHeader,
        tradeFooter,
        addressData,
        subscription,
      } = user;
      this.setUserDetails({
        customerType,
        customerTypeLogo,
        firstName,
        lastName,
        activated: metaData.activated,
        unitCode,
        tradeHeader,
        tradeFooter,
        latitude: addressData.latitude,
        longitude: addressData.longitude,
        subscription: {
          type: subscription.type,
          validUntil: subscription.validUntil,
        },
      });
    }
  };

  getCurrentUser = (): CurrentUser | null => {
    return this.currentUser;
  };

  get isAuthorized(): boolean {
    return this.currentUser !== null;
  }

  get isGotDetails(): boolean {
    return this.userDetails !== null;
  }

  get isAccountActivated(): boolean {
    if (this.userDetails) {
      return this.userDetails.activated;
    }

    return false;
  }

  get isTokenExpired(): boolean {
    if (!this.currentUser) {
      return false;
    }

    if (this.currentUser.expiresAt < Date.now()) {
      return true;
    }

    return false;
  }

  get timeUntilExpiry(): number {
    if (!this.currentUser) {
      return 0;
    }
    // Calculate time until expiry in milliseconds
    return this.currentUser.expiresAt - Date.now();
  }

  get shouldRefreshToken(): boolean {
    if (!this.currentUser) {
      return false;
    }

    // Refresh token 1 minute (60,000 milliseconds) before expiry
    // or if it has already expired.
    const oneMinute = 60 * 1000;
    const timeUntilExpiry = this.timeUntilExpiry;

    return timeUntilExpiry <= oneMinute;
  }

  refreshToken = async (): Promise<boolean> => {
    if (!this.currentUser) {
      return false;
    }

    // Prevent multiple simultaneous refresh calls
    if (this.isRefreshTokenLoading) {
      return false;
    }

    this.setIsRefreshTokenLoading(true);

    try {
      const { data: refreshResult } = await axios.post(
        process.env.REACT_APP_API_REFRESH_TOKEN || "",
        {
          refreshToken: this.currentUser.refreshToken,
        },
        {
          headers: {
            Authorization: `${this.tokenType} ${this.currentUser.token}`,
            "Content-Type": "application/json",
          },
        }
      );

      // Check if refresh was successful
      if (refreshResult?.type === "success") {
        // Update the token with new expiresAt from backend
        this.setCurrentUser(refreshResult.body);
        this.setIsRefreshTokenLoading(false);
        return true;
      } else {
        // If refresh result isn't "success", logout
        this.setIsRefreshTokenLoading(false);
        this.setCurrentUser(null);
        this.setUserDetails(null);
        this.setCurrentFmcToken("");
        this.setBackendFcmToken("");
        this.rootStore.deliveryPinStore.setDeliveryPin("");
        window.location.href = "/login";
        return false;
      }
    } catch (refreshError: unknown) {
      this.setIsRefreshTokenLoading(false);

      // Check if it's a network error vs auth error
      if (refreshError instanceof Error) {
        const axiosError = refreshError as AxiosError;
        const status = axiosError?.response?.status;

        // If it's an auth error (401, 403), logout immediately
        if (status === 401 || status === 403) {
          this.setCurrentUser(null);
          this.setUserDetails(null);
          this.setCurrentFmcToken("");
          this.setBackendFcmToken("");
          this.rootStore.deliveryPinStore.setDeliveryPin("");
          window.location.href = "/login";
          return false;
        }
        // For network errors, return false - let axios interceptor handle retry
        return false;
      }

      return false;
    }
  };

  logout = async (): Promise<boolean> => {
    if (!this.currentUser) {
      return false;
    }

    const requestOptions = {
      method: "POST",
      headers: {
        Authorization: `${this.tokenType} ${this.currentUser?.token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ deviceId: this.currentDeviceId }),
    };

    this.currentUser = null;

    try {
      const endpoint = process.env.REACT_APP_API_LOGOUT || null;

      if (!endpoint) {
        // eslint-disable-next-line no-console
        console.warn("REACT_APP_API_LOGOUT is not defined");

        return false;
      }

      const response = await fetch(endpoint, requestOptions);

      if (!response.ok) {
      }

      queryClient.clear();
      api.defaults.headers.common.Authorization = null;
      this.setCurrentUser(null);
      this.setUserDetails(null);
      this.setBackendFcmToken("");
      this.setCurrentFmcToken("");
      this.rootStore.deliveryPinStore.setDeliveryPin("");
      deleteToken(messaging);

      unregisterServiceWorker();

      return true;
    } catch (error) {
      queryClient.clear();
      this.setCurrentUser(null);
      this.setUserDetails(null);
      this.setBackendFcmToken("");
      this.setCurrentFmcToken("");
      this.rootStore.deliveryPinStore.setDeliveryPin("");

      deleteToken(messaging);

      unregisterServiceWorker();

      return false;
    }
  };

  saveFcmToken = async (
    fcmToken: string,
    channel: string,
    osType: string,
    deviceId: string,
    model: string
  ): Promise<LoginResponse | null> => {
    this.setFcmTokenLoading(true);

    const getOsType = (): string => {
      const UserAgent = window.navigator.userAgent;

      if (/Win/i.test(UserAgent)) {
        return "WINDOWS";
      }
      if (/Mac/i.test(UserAgent)) {
        return "MAC_OS";
      }
      if (/Linux/i.test(UserAgent)) {
        return "LINUX";
      }
      if (/Android/i.test(UserAgent)) {
        return "ANDROID";
      }
      if (/iOS|iPadOS|iPhone OS/i.test(UserAgent)) {
        return "IOS";
      }
      return "WINDOWS";
    };
    async function getDeviceId(): Promise<string> {
      const buffer = await crypto.subtle.digest(
        "SHA-256",
        new TextEncoder().encode(navigator.userAgent)
      );
      const hexCodes = Array.from(new Uint8Array(buffer))
        .map((b) => b.toString(16).padStart(2, "0"))
        .join("");
      // console.log(hexCodes);
      return hexCodes;
    }
    function getDeviceType(): string {
      const UserAgent = window.navigator.userAgent;

      if (/Mobile/.test(UserAgent)) {
        return "Mobile";
      }
      if (/Tablet/.test(UserAgent)) {
        return "Tablet";
      }
      return "Desktop";
    }

    this.setCurrentOsType(getOsType());
    this.setCurrentDeviceType(getDeviceType());
    const newDeviceId = await getDeviceId();
    this.setCurrentDeviceID(newDeviceId);

    const requestOptions = {
      method: "POST",
      headers: {
        Authorization: `${this.tokenType} ${this.currentUser?.token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        token: fcmToken,
        channel,
        osType,
        deviceId,
        model,
      }),
    };

    try {
      const endpoint = process.env.REACT_APP_API_SAVE_FCM_TOKEN || null;

      if (!endpoint) {
        // eslint-disable-next-line no-console
        console.warn("REACT_APP_API_SAVE_FCM_TOKEN is not defined");

        return null;
      }

      const response = await fetch(endpoint, requestOptions);
      const data = await response.json();
      // console.log(response);
      if (data.type === "success") {
        this.setBackendFcmToken(fcmToken);
        this.setFcmTokenLoading(false);
      }

      return data;
    } catch (error) {
      this.setFcmTokenLoading(false);
      return null;
    }
  };
}

export default UserStore;
